import {
  AuthError,
  type AuthClient,
  type AuthSession,
  type AuthStateListener,
  type SignInResult,
} from "./types";

/**
 * AuthClient backed by the in-house `company-auth` package on the Django
 * backend. The flow is two-step and 2FA-mandatory by design:
 *
 *   1. POST /login/        — verify email + password. Sets NO session;
 *      returns `{ id, ticket, qr_code, skip_2fa }`. `qr_code` is a TOTP-enrollment
 *      QR for first-time users. If a remembered-device token is presented
 *      and still valid, `skip_2fa` is true and cookies ARE set here.
 *   2. POST /login/two_fa/ — verify the TOTP code with the ticket from
 *      step 1 (5 minutes, 5 codes, then 404 → start over); sets the HttpOnly JWT
 *      cookies. `remember_device` registers this browser for 30 days so
 *      step 2 can be skipped next time.
 *
 * Session cookies ride on every request via `credentials: "include"`.
 * The app-level identity is read from `GET /me/`.
 */
export interface CompanyAuthClientOptions {
  /** Backend origin, e.g. "http://localhost:8000". */
  baseUrl: string;
  /** Exposed only so tests can inject a stub fetch. */
  fetch?: typeof fetch;
}

interface MeResponse {
  id: number | string;
  email: string;
}

const DEVICE_ID_KEY = "vidario.device-id";

function readDeviceId(): string | null {
  if (typeof localStorage === "undefined") return null;
  return localStorage.getItem(DEVICE_ID_KEY);
}

function ensureDeviceId(): string {
  const existing = readDeviceId();
  if (existing) return existing;
  const id = crypto.randomUUID();
  if (typeof localStorage !== "undefined") localStorage.setItem(DEVICE_ID_KEY, id);
  return id;
}

/** Why `/password-set/` refused (#21). The backend answers 400 in the
 * drf-standardized-errors shape; `attr` names the field: `token`/`base64_id`
 * for the link, `password` for the password rules. */
async function passwordSetError(res: Response): Promise<AuthError> {
  const body = (await res.json().catch(() => null)) as {
    errors?: { attr?: string | null; detail?: string }[];
  } | null;
  const errors = res.status === 400 ? (body?.errors ?? []) : [];
  if (errors.some((e) => e.attr === "token" || e.attr === "base64_id")) {
    return new AuthError("link_expired", "Dieser Link ist abgelaufen oder ungültig.");
  }
  const rules = errors.filter((e) => e.attr === "password" && e.detail).map((e) => e.detail!.trim());
  if (rules.length > 0) return new AuthError("weak_password", rules.join(" "));
  return new AuthError("unknown", "Passwort konnte nicht gesetzt werden.");
}

const SECONDS_PER_MINUTE = 60;

/** A 429 from company-auth's or Vidario's rate limits (#67): say how long to
 * wait, from `Retry-After` (seconds), rounded up to whole minutes. */
function rateLimitedError(res: Response): AuthError {
  const seconds = Number(res.headers.get("Retry-After"));
  if (!Number.isFinite(seconds) || seconds <= 0) {
    return new AuthError("rate_limited", "Zu viele Versuche. Bitte später erneut versuchen.");
  }
  const minutes = Math.ceil(seconds / SECONDS_PER_MINUTE);
  const wait = minutes === 1 ? "1 Minute" : `${minutes} Minuten`;
  return new AuthError("rate_limited", `Zu viele Versuche. Bitte in ${wait} erneut versuchen.`);
}

export function createCompanyAuthClient(
  options: CompanyAuthClientOptions,
): AuthClient {
  const { baseUrl, fetch: fetchFn = fetch } = options;
  const listeners = new Set<AuthStateListener>();

  function emit(session: AuthSession | null): void {
    for (const cb of listeners) cb(session);
  }

  async function call(path: string, init?: RequestInit): Promise<Response> {
    // Pull `headers` out of `init` so the spread below can't clobber the
    // merged header object (which would drop Content-Type → DRF 415).
    const { headers: extraHeaders, ...rest } = init ?? {};
    try {
      return await fetchFn(`${baseUrl}${path}`, {
        credentials: "include",
        ...rest,
        headers: {
          Accept: "application/json",
          ...(init?.body ? { "Content-Type": "application/json" } : {}),
          ...extraHeaders,
        },
      });
    } catch (cause) {
      throw new AuthError("network", "Network request failed.", cause);
    }
  }

  /** Resolves the current session from `/me/`, or `null` on 401. */
  async function fetchMe(): Promise<AuthSession | null> {
    const res = await call("/me/");
    if (res.status === 401) return null;
    if (!res.ok) {
      throw new AuthError("unknown", `GET /me/ failed with ${res.status}`);
    }
    const me = (await res.json()) as MeResponse;
    return { userId: String(me.id), email: me.email };
  }

  return {
    async getSession() {
      const session = await fetchMe();
      if (session) return session;
      // Access token may be expired — try one cookie refresh, then retry.
      const refresh = await call("/cookies-token-refresh/", { method: "POST" });
      if (!refresh.ok) return null;
      return fetchMe();
    },

    async signInWithPassword(email, password): Promise<SignInResult> {
      const deviceId = readDeviceId();
      const res = await call("/login/", {
        method: "POST",
        body: JSON.stringify({ email, password }),
        headers: deviceId ? { "X-Device-Token": deviceId } : {},
      });
      // company-auth masks "user not found" as 404; treat 400/401/404 as
      // a generic credential failure so we don't leak which emails exist.
      if (res.status === 400 || res.status === 401 || res.status === 404) {
        throw new AuthError("invalid_credentials", "Ungültige Anmeldedaten.");
      }
      if (res.status === 429) {
        throw rateLimitedError(res);
      }
      if (!res.ok) {
        throw new AuthError("unknown", `Login failed with ${res.status}`);
      }
      const body = (await res.json()) as {
        id: number | string;
        ticket: string;
        qr_code?: string;
        skip_2fa?: boolean;
      };
      if (body.skip_2fa === true) {
        // Remembered device — cookies were set by /login/ itself.
        const session = await fetchMe();
        if (!session) {
          throw new AuthError("unknown", "Keine Sitzung nach dem Login.");
        }
        emit(session);
        return { status: "authenticated", session };
      }
      return {
        status: "two_fa_required",
        ticket: body.ticket,
        qrCode: body.qr_code ? body.qr_code : null,
      };
    },

    async verifyTwoFa(ticket, code, opts) {
      const rememberDevice = opts?.rememberDevice ?? false;
      const deviceId = rememberDevice ? ensureDeviceId() : undefined;
      const res = await call("/login/two_fa/", {
        method: "POST",
        body: JSON.stringify({
          ticket,
          code,
          ...(rememberDevice && deviceId
            ? { remember_device: true, device_id: deviceId }
            : {}),
        }),
      });
      if (res.status === 403) {
        throw new AuthError("invalid_2fa_code", "Der Code ist ungültig.");
      }
      if (res.status === 404) {
        throw new AuthError("challenge_expired", "Anmeldung abgelaufen — bitte erneut anmelden.");
      }
      if (res.status === 429) {
        throw rateLimitedError(res);
      }
      if (!res.ok) {
        throw new AuthError("unknown", `2FA failed with ${res.status}`);
      }
      const session = await fetchMe();
      if (!session) {
        throw new AuthError("unknown", "Keine Sitzung nach der 2FA-Bestätigung.");
      }
      emit(session);
      return session;
    },

    async signOut() {
      // Best effort on the server: signing out must work while the backend is
      // unreachable too, e.g. from the profile error card (#23).
      try {
        await call("/logout/", { method: "POST" });
      } catch {
        // The cookies stay until they expire; the app forgets the session now.
      } finally {
        emit(null);
      }
    },

    async resetPasswordForEmail(email) {
      // Vidario's own /password-reset/ — works for any role.
      // company-auth's /admin/reset-password/ is staff-only and 403s
      // for recruiters, so we side-step it.
      const res = await call("/password-reset/", {
        method: "POST",
        body: JSON.stringify({ email }),
      });
      // Stay neutral about whether the address exists; only a rate limit
      // (per IP, so it reveals nothing about the address) and a server
      // error are surfaced.
      if (res.status === 429) {
        throw rateLimitedError(res);
      }
      if (res.status >= 500) {
        throw new AuthError("unknown", "Zurücksetzen fehlgeschlagen.");
      }
    },

    async updatePassword(newPassword) {
      // Vidario's own /password-set/ — same payload shape as company-auth's
      // /admin/set-password/, just routed through our own view so any
      // role can complete the reset.
      const params =
        typeof window !== "undefined"
          ? new URLSearchParams(window.location.search)
          : new URLSearchParams();
      const base64Id = params.get("uid");
      const token = params.get("token");
      if (!base64Id || !token) {
        throw new AuthError(
          "unknown",
          "Der Reset-Link ist ungültig oder abgelaufen.",
        );
      }
      const res = await call("/password-set/", {
        method: "POST",
        body: JSON.stringify({
          base64_id: base64Id,
          token,
          password1: newPassword,
          password2: newPassword,
        }),
      });
      if (res.status === 429) {
        throw rateLimitedError(res);
      }
      if (!res.ok) {
        throw await passwordSetError(res);
      }
    },

    onAuthStateChange(listener: AuthStateListener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}
